import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmationModal from ".";
import {
  GetOrders200Response,
  InstrumentTypeResponse,
  OrderStatusResponse,
} from "../../codegen-api";
import { COLORS } from "../../constants/design/colors";
import {
  type CancelAllOrdersResponse,
  useOrder,
} from "../../hooks/api/order/useOrder";
import { useToast } from "../../hooks/toast";
import { useSFX } from "../../hooks/useSFX";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { IBaseModalProps } from "../BaseModal";
import { CancelText } from "./style";
import { AssetResponse } from "../../utils/asset";
import { ButtonThemeEnum } from "../Buttons/styles";
import { SPACING } from "../../constants/design/spacing";
import { usePerpOrder } from "../../hooks/api/order/usePerpOrder";
import { useSpotOrder } from "../../hooks/api/order/useSpotOrder";
import { useInstrumentResolver } from "../../hooks/markets/useInstrumentResolver";
import { getCurrentChainConfig } from "../../config/chains";

type ICancelAllOrdersModalProps = IBaseModalProps & {
  instrumentType?: InstrumentTypeResponse;
  asset?: AssetResponse;
  orders?: GetOrders200Response[];
};

const isOpenOrder = (order: GetOrders200Response) => {
  const status = String(order.order_status || "").toLowerCase();
  return (
    !status ||
    status === String(OrderStatusResponse.Opened).toLowerCase() ||
    status === String(OrderStatusResponse.Partial).toLowerCase() ||
    status === "open" ||
    status === "partially_filled"
  );
};

const isPerpOrder = (order: GetOrders200Response) =>
  order.instrument_type === InstrumentTypeResponse.Perpetual ||
  String(order.instrument_name || "").toUpperCase().endsWith("-PERP");

const matchesAsset = (order: GetOrders200Response, asset?: AssetResponse) => {
  const requestedAsset = String(asset || "").toUpperCase();
  if (!requestedAsset) return true;
  return String(order.instrument_name || "")
    .toUpperCase()
    .startsWith(`${requestedAsset}-`);
};

export function CancelAllOrdersModal({
  instrumentType,
  asset,
  orders,
  onHide,
  ...others
}: ICancelAllOrdersModalProps) {
  const { removeAllOrders } = useOrder();
  const { cancelPerpOrdersBatch } = usePerpOrder();
  const { cancelSpotOrdersBatch } = useSpotOrder();
  const { resolveMarket } = useInstrumentResolver();
  const { addToast, addErrorToast } = useToast();
  const { playSound } = useSFX();
  const { t } = useTranslation("app", {
    keyPrefix: "ConfirmationModal.CancelAllOrdersModal",
  });
  const { t: apiErrors } = useTranslation("apiErrors");
  const [isLoading, setLoading] = useState<boolean>(false);

  const formatFailedOrders = useCallback(
    (response: CancelAllOrdersResponse) => {
      const failedIds = response.failed || [];
      const detailsById = new Map(
        (response.failed_details || [])
          .filter((detail) => detail.order_id)
          .map((detail) => [String(detail.order_id), detail])
      );

      return failedIds
        .map((id) => {
          const detail = detailsById.get(String(id));
          const market = detail?.instrument_name ? `${detail.instrument_name}: ` : "";
          return `#${id}: ${market}${detail?.reason || t("unknown_cancel_error")}`;
        })
        .join("\n");
    },
    [t]
  );

  const cancelAllOrders = useCallback(async () => {
    try {
      setLoading(true);

      // Route each order type to the path that can actually cancel it: PERP orders cancel ON-CHAIN
      // via a single signed batch (gasless / one-click) because the backend signer is NOT an
      // authorized operator for user-owned perp orders; SPOT orders cancel via the backend. Any perp
      // rejection the backend reports is ignored — those are handled by the gasless path above.
      const openOrders = (orders || [])
        .filter(isOpenOrder)
        .filter((order) => matchesAsset(order, asset));
      const perpOrderIds = openOrders
        .filter(isPerpOrder)
        .map((order) => order.order_id)
        .filter((orderId): orderId is string => !!orderId && /^\d+$/.test(orderId));
      const hasSpotOrders = openOrders.some((order) => !isPerpOrder(order));
      const haveOrderList = openOrders.length > 0;

      const cancelled: string[] = [];
      const failedDetails: NonNullable<CancelAllOrdersResponse["failed_details"]> = [];

      if (perpOrderIds.length > 0) {
        const perpResult = await cancelPerpOrdersBatch(perpOrderIds);
        cancelled.push(...(perpResult.cancelled || []));
      }

      // Spot orders: cancel gaslessly through the relayer, per pool. This only touches the orders
      // shown here, so it's asset-scoped and never wipes unrelated markets. The account-wide backend
      // cancel-all is used only as a fallback on chains without a relayer.
      const spotOrders = openOrders.filter((order) => !isPerpOrder(order));
      const config = getCurrentChainConfig();
      const canGaslessSpot = !!(config.gasless && config.relayerUrl);

      if (spotOrders.length > 0 && canGaslessSpot) {
        const withCurrencies = spotOrders.map((order) => {
          const market = resolveMarket(order) as
            | { base_currency?: string; quote_currency?: string }
            | undefined;
          return {
            order_id: order.order_id,
            instrument_id: order.instrument_id,
            base_currency: market?.base_currency,
            quote_currency: market?.quote_currency,
          };
        });
        const spotResult = await cancelSpotOrdersBatch(withCurrencies);
        cancelled.push(...(spotResult.cancelled || []));
        (spotResult.failed_details || []).forEach((detail: any) => failedDetails.push(detail));
      } else if (!haveOrderList || hasSpotOrders) {
        // No relayer (or no order list) — fall back to the backend cancel-all; filter perp rejections.
        const spotResult = await removeAllOrders(instrumentType, asset);
        cancelled.push(
          ...((spotResult.cancelled ?? spotResult.order_ids ?? []) as string[])
        );
        (spotResult.failed_details || []).forEach((detail) => {
          const detailIsPerp =
            detail.instrument_type === InstrumentTypeResponse.Perpetual ||
            String(detail.instrument_name || "").toUpperCase().endsWith("-PERP");
          if (!detailIsPerp) failedDetails.push(detail);
        });
      }

      const failed = failedDetails
        .map((detail) => String(detail.order_id))
        .filter(Boolean);
      const cancelledCount = cancelled.length;
      const failedCount = failed.length;

      if (failedCount > 0) {
        addErrorToast(
          cancelledCount > 0 ? t("cancel_partial_failed") : t("cancel_failed"),
          formatFailedOrders({
            success: cancelledCount > 0,
            cancelled,
            failed,
            failed_details: failedDetails,
          })
        );
      } else if (cancelledCount > 0 || perpOrderIds.length > 0) {
        playSound("whoosh", true);
        addToast({
          type: ToastEnum.SIMPLE,
          header: t("cancel_success"),
          subheader: t("cancel_success_count", { count: cancelledCount }),
          status: ToastStatusEnum.SUCCESS,
        });
      }
    } catch (error: any) {
      addErrorToast(
        t("cancel_failed"),
        apiErrors(error.message) || t("place_order_again")
      );
    } finally {
      setLoading(false);
    }
    onHide?.();
  }, [
    addErrorToast,
    addToast,
    apiErrors,
    asset,
    cancelPerpOrdersBatch,
    cancelSpotOrdersBatch,
    resolveMarket,
    formatFailedOrders,
    instrumentType,
    onHide,
    orders,
    removeAllOrders,
    t,
  ]);

  return (
    <ConfirmationModal
      {...others}
      onHide={onHide}
      primaryColor={COLORS.system.one}
      title={t("cancel_all_orders_title")}
      extras={<CancelText>{t("cancel_all_orders_text")}</CancelText>}
      isLoading={isLoading}
      confirmationButtons={[
        {
          title: t("confirm"),
          onClick: cancelAllOrders,
          disabled: false,
          style: {
            marginBottom: SPACING.three,
          },
        },
        {
          title: t("cancel"),
          onClick: onHide,
          disabled: false,
          theme: ButtonThemeEnum.SYSTEM,
        },
      ]}
    />
  );
}
